import { PRAISE_BEHAVIOR_MAX, PRAISE_MAX_CORE_VALUES, PRAISE_MAX_FRAMEWORKS } from '@cult/domain';
import { z } from 'zod';
import {
  BaseEntitySchema,
  CursorPageSchema,
  CursorQuerySchema,
  createdRangeFields,
  englishText,
  englishTitleField,
  idField,
  idMultiField,
  qField,
  timeZoneField,
  updatedAtField,
} from './common';
import { ReferenceEmbedSchema, tagIdsField } from './reference';
import { PublicAttachmentSchema, attachmentsInputField } from './uploads';
import { type UserSummary, UserSummarySchema } from './users';

export const PRAISE_PERIODS = {
  ALL: 'all',
  WEEK: 'week',
  MONTH: 'month',
} as const;
export type PraisePeriods = (typeof PRAISE_PERIODS)[keyof typeof PRAISE_PERIODS];
export const PRAISE_PERIOD_VALUES = Object.values(PRAISE_PERIODS) as [PraisePeriods, ...PraisePeriods[]];
export const PraisePeriodSchema = z.enum(PRAISE_PERIOD_VALUES);

// Each tag list may be empty on its own — a praise needs at least one tag across core values and
// frameworks combined (PRAISE_TAG_REQUIRED).
const coreValuesField = tagIdsField(PRAISE_MAX_CORE_VALUES, 'Core values');
const frameworksField = tagIdsField(PRAISE_MAX_FRAMEWORKS, 'Frameworks');
export const PRAISE_TAG_REQUIRED = 'Tag at least one core value or framework';
const behaviorField = englishText(z.string().trim().min(1).max(PRAISE_BEHAVIOR_MAX));
const whatHappenedField = englishText(z.string().trim().min(1).max(5000));

export const PraiseCreateInputSchema = z
  .object({
    receiver: idField,
    coreValues: coreValuesField.default([]),
    frameworks: frameworksField.default([]),
    title: englishTitleField,
    behavior: behaviorField,
    whatHappened: whatHappenedField,
    attachments: attachmentsInputField,
  })
  .refine((d) => d.coreValues.length + d.frameworks.length > 0, {
    message: PRAISE_TAG_REQUIRED,
    path: ['coreValues'],
  });
export type PraiseCreateInput = z.infer<typeof PraiseCreateInputSchema>;

export const PraiseUpdateInputSchema = z
  .object({
    receiver: idField.optional(),
    coreValues: coreValuesField.optional(),
    frameworks: frameworksField.optional(),
    title: englishTitleField.optional(),
    behavior: behaviorField.optional(),
    whatHappened: whatHappenedField.optional(),
    attachments: attachmentsInputField,
  })
  .refine((d) => Object.keys(d).length > 0, { message: 'At least one field is required' });
export type PraiseUpdateInput = z.infer<typeof PraiseUpdateInputSchema>;

export const PraiseUserEmbedSchema = UserSummarySchema;
export type PraiseUserEmbed = UserSummary;

export const PublicPraiseSchema = BaseEntitySchema.extend({
  updatedAt: updatedAtField,
  number: z.number().int(),
  author: PraiseUserEmbedSchema,
  receiver: PraiseUserEmbedSchema,
  coreValues: z.array(ReferenceEmbedSchema),
  frameworks: z.array(ReferenceEmbedSchema),
  title: z.string(),
  behavior: z.string(),
  whatHappened: z.string(),
  attachments: z.array(PublicAttachmentSchema),
});
export type PublicPraise = z.infer<typeof PublicPraiseSchema>;

export const PraiseListQuerySchema = CursorQuerySchema.extend({
  // Admin table uses offset pagination (page jumps + total); the feed keeps `cursor`.
  offset: z.coerce.number().int().min(0).optional(),
  period: PraisePeriodSchema.optional(),
  coreValue: idField.optional(),
  coreValues: idMultiField,
  frameworks: idMultiField,
  author: idField.optional(),
  receiver: idField.optional(),
  q: qField,
  ...createdRangeFields,
});
export type PraiseListQuery = z.infer<typeof PraiseListQuerySchema>;

export const PraiseExportQuerySchema = PraiseListQuerySchema.omit({ cursor: true, limit: true, offset: true }).extend({
  tz: timeZoneField,
});
export type PraiseExportQuery = z.infer<typeof PraiseExportQuerySchema>;

export const PraiseListResponseSchema = CursorPageSchema.extend({
  praises: z.array(PublicPraiseSchema),
  // Present only in offset (admin) mode; the cursor feed omits it.
  total: z.number().int().nonnegative().optional(),
});
export type PraiseListResponse = z.infer<typeof PraiseListResponseSchema>;
